import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/auth.store'
import { SympheniaLogo } from '@/components/SympheniaLogo'
import { InstallButton } from '@symphenia/ui-components'
import { INSTALL_TEXTS } from '@/components/install-texts'
import { authApi } from '@/lib/auth.api'
import type { LoginResponse, ZweiterSchritt } from '@/lib/auth.types'
import { ZweiterFaktorForm } from '../components/ZweiterFaktorForm'
import { ZweiFaktorEinrichtung } from '@/components/zwei-faktor/ZweiFaktorEinrichtung'

interface LocationState {
  from?: { pathname: string; search?: string; hash?: string }
}

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, schliesseAnmeldungAb, isLoading, isAuthenticated, error, clearError } = useAuthStore()
  // AP-38-E: Nach dem Passwort fehlt noch ein Schritt — zweiter Faktor oder Einrichtung.
  const [schritt, setSchritt] = useState<ZweiterSchritt | null>(null)
  const [nachEinrichtung, setNachEinrichtung] = useState<LoginResponse | null>(null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // AP-12: Hinweis nach Klick auf den E-Mail-Bestätigungslink (?verified=true|false)
  const verifiedParam = new URLSearchParams(location.search).get('verified')
  const verifiedMsg =
    verifiedParam === 'true'
      ? { type: 'success' as const, text: t('auth.emailVerified') }
      : verifiedParam === 'false'
        ? { type: 'error' as const, text: t('auth.emailVerifyFailed') }
        : null

  // Mit Query und Anker: ein Deep-Link wie `/projects/…?tab=positions&position=…`
  // kommt beim Neuladen über diese Seite (Sitzung erneuern) und verlor sie bis
  // AP-35 Phase F — geöffnet wurde dann die Übersicht statt der Position.
  const ziel = (location.state as LocationState)?.from
  const from = ziel ? `${ziel.pathname}${ziel.search ?? ''}${ziel.hash ?? ''}` : '/'

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true })
  }, [isAuthenticated, navigate, from])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    try {
      const offen = await login({ email, password })
      if (offen) {
        setSchritt(offen)
        return
      }
      navigate(from, { replace: true })
    } catch {
      // error is in store
    }
  }

  const angemeldet = async (data: LoginResponse) => {
    await schliesseAnmeldungAb(data)
    navigate(from, { replace: true })
  }

  const zurueck = () => {
    setSchritt(null)
    setNachEinrichtung(null)
    setPassword('')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="text-center">
          <div className="mb-3 flex justify-center">
            <SympheniaLogo size={24} context="content" />
          </div>
          <p className="text-sm text-muted-foreground">{t('auth.loginSubtitle')}</p>
        </div>

        {/* AP-12: Verifizierungs-Hinweis */}
        {verifiedMsg && (
          <div
            className={
              verifiedMsg.type === 'success'
                ? 'rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400'
                : 'rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive'
            }
          >
            {verifiedMsg.text}
          </div>
        )}

        {schritt?.schritt === 'ZWEITER_FAKTOR' && (
          <ZweiterFaktorForm schritt={schritt} onAngemeldet={angemeldet} onZurueck={zurueck} />
        )}

        {schritt?.schritt === 'EINRICHTEN' && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-foreground">{t('zweiFaktor.einrichtenTitel')}</h2>
            <p className="text-sm text-muted-foreground">{t('zweiFaktor.einrichtenPflicht')}</p>
            <ZweiFaktorEinrichtung
              mitGeraet
              starte={async (verfahren) => (await authApi.startSetup(schritt.ticket, verfahren)).data}
              bestaetige={async (code, merken) => {
                const { data } = await authApi.confirmSetup(schritt.ticket, code, merken)
                // Angemeldet wird erst nach „Fertig" — vorher die Codes sichern.
                setNachEinrichtung(data)
                return data.recoveryCodes ?? []
              }}
              onFertig={() => nachEinrichtung && void angemeldet(nachEinrichtung)}
              onAbbrechen={zurueck}
            />
          </div>
        )}

        {!schritt && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error banner */}
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="name@beispiel.de"
            />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                {t('auth.password')}
              </label>
              <Link
                to="/forgot-password"
                // Nicht in der Tab-Reihenfolge: E-Mail → Passwort → Anmelden (Auftraggeber 18.9.2026).
                tabIndex={-1}
                className="text-xs text-primary hover:underline"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? t('common.loading') : t('auth.login')}
          </Button>
        </form>
        )}

        {/* AP-17: „App installieren"-Button – nativer Ein-Klick auf Chrome/Edge,
            geführte Anleitung als Fallback. Verschwindet im Standalone-Modus. */}
        <div className="flex justify-center pt-2">
          <InstallButton texts={INSTALL_TEXTS} />
        </div>
      </div>
    </div>
  )
}
